/**
 * Spam filter cho các form gửi thẳng tới Telegram (Contact / Booking / OrderBook).
 *
 * Chạy client-side, mục tiêu: giảm noise trong Telegram của mình, KHÔNG phải
 * anti-bot tuyệt đối (bot vẫn có thể POST thẳng api.telegram.org với token public).
 *
 * Logic: chỉ reject khi có "signature" của generator, cố gắng không giết reader thật.
 *   1) honeypot      -> input ẩn mà người thật không thấy, bot thì fill mọi field
 *   2) too-fast      -> submit < 1.2s sau khi form render (người thật phải gõ 4 field)
 *   3) junk-email    -> dot-stuffing: w.u.j.o.n.ak.38.9@gmail.com (nhiều chấm + toàn segment ngắn)
 *                        nhưng nguyen.thi.hong.thai.2015 (segment dài) vẫn an toàn
 *   4) junk-content  -> name/subject/message là random string: không space, không dấu Việt,
 *                        mixed case, >= 12 ký tự (iQQJLmqGhUjutypunLkdCpxg) -> cần >= 2 field
 */

const MIN_SUBMIT_MS = 1200;
const GIBBERISH_MIN_LEN = 12;
const MIN_DOTS = 4; // nhiều hơn 3 dấu chấm trong local part
const VIETNAMESE = /[\u00C0-\u00FF\u0100-\u017F\u1EA0-\u1EF9\u0300-\u0362]/; // mọi ký tự có dấu (Việt/Tây) -> không phải random

/** true khi local part looksLikeGenerated: "w.u.j.o.n.ak.38.9" */
export function isGeneratedEmail(email = "") {
  const local = String(email || "").trim().toLowerCase().split("@")[0];
  if (!local) return false;
  const parts = local.split(".");
  const dots = parts.length - 1;
  const shortParts = parts.filter((p) => p.length <= 2).length;
  return dots >= MIN_DOTS && shortParts >= 3;
}

/** true khi string looksLikeGenerated random: "iQQJLmqGhUjutypunLkdCpxg" */
export function looksLikeGenerated(str = "") {
  const s = String(str || "").trim();
  if (s.length < GIBBERISH_MIN_LEN) return false;
  if (/\s/.test(s)) return false; // câu thật luôn có space
  if (VIETNAMESE.test(s)) return false; // có dấu Việt -> người thật
  // mixed case bất thường (iQ, dG, GOwn) hoặc toàn consonant cluster dài
  const mixedCase = /[a-z][A-Z]|[A-Z][a-z](?=[A-Z])/.test(s);
  if (mixedCase) return true;
  const longestConsonants = (s.toLowerCase().match(/[^aeiou0-9]{4,}/g) || []).reduce(
    (a, b) => Math.max(a, b.length),
    0
  );
  return longestConsonants >= 5;
}

/**
 * { spam: boolean, reason: string }
 * Unnamed params cho Contact: { email, name, subject, message, honeypot, elapsedMs }
 * Cho Booking/OrderBook: { email, name, message } (message = note/ghi chú)
 */
export function checkContactSpam({
  email = "",
  name = "",
  subject = "",
  message = "",
  honeypot = "",
  elapsedMs = Infinity,
} = {}) {
  if (String(honeypot || "").trim()) {
    return { spam: true, reason: "honeypot" };
  }
  if (Number.isFinite(elapsedMs) && elapsedMs < MIN_SUBMIT_MS) {
    return { spam: true, reason: "too-fast" };
  }
  if (isGeneratedEmail(email)) {
    return { spam: true, reason: "junk-email" };
  }
  const generatedFields = [name, subject, message].filter(looksLikeGenerated).length;
  if (generatedFields >= 2) {
    return { spam: true, reason: "junk-content" };
  }
  return { spam: false, reason: "" };
}

/**
 * Log + đếm spam đã bị drop trong ngày (sessionStorage), để sau này có thể
 * piggyback 1 dòng digest lên message legit kế tiếp, hoặc để audit thủ công.
 */
const SPAM_KEY = "lht_spam_log";

export function recordDroppedSpam(payload = {}, reason = "") {
  const entry = {
    at: Date.now(),
    reason,
    email: payload.email || "",
    name: payload.name || "",
    subject: payload.subject || "",
  };
  try {
    const raw = sessionStorage.getItem(SPAM_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const kept = list.filter((e) => e.at >= cutoff).concat(entry);
    sessionStorage.setItem(SPAM_KEY, JSON.stringify(kept.slice(-200)));
  } catch (e) {
    /* ignore */
  }
  // eslint-disable-next-line no-console
  console.log(`[spam-filter] dropped (${reason})`, entry);
  return entry;
}

export function getDroppedSpamToday() {
  try {
    const raw = sessionStorage.getItem(SPAM_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return list.filter((e) => e.at >= cutoff);
  } catch (e) {
    return [];
  }
}
